import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ScriptureTopic, ScriptureTopicVerse } from '@/types/scripture';
import type { Prayer } from '@/types/prayer';
import { getAllActiveTopics, getVersesForTopic } from '@/lib/scripture';
import { searchTopics } from '@/lib/topic-search';
import { getVersesForPrayer } from '@/lib/verse-result';
import { VerseSuggestError, type SuggestedVerse } from '@/lib/verse-suggest';
import { ensureBibleHydrated } from '@/lib/bible/active';
import {
  loadTopics,
  resolveTopicVerseText,
  type BundledTopic,
  type BundledTopicVerse,
} from '@/lib/bible-search/topics';
import styles from './ShareVersePicker.module.css';

// The picker's stable output contract.
//
// `text` and `reference` are the active fields consumed by
// LetThemKnowSheet to render the verse on the share card.
//
// The four structured fields (`bookName`, `chapter`, `verseStart`,
// `verseEnd`) were originally added for the LetThemKnowSheet Q3 spine
// catalog lookup (Ship 2a → 2a.3, via
// src/lib/scripture.ts:fetchSpineProseForVerse). That consumer was
// retired on 2026-05-23 (operator pivot away from a story surface on
// share cards). The four fields are intentionally retained on the
// emit shape — they parallel the dormant helper's input shape per
// CLAUDE.md rule #77, so any future pull-based discovery surface can
// revive the spine lookup without re-plumbing the picker producer.
// See CLAUDE.md "Banked code edits" for the dormant-spine-helper
// entry. `bookName` is the canonical book name (e.g., "Psalm", "Song
// of Solomon") matching ScriptureTopicVerse.book; the spine helper
// converts it to a slug via CANONICAL_BOOKS.
export interface SelectedVerse {
  text: string;
  reference: string;
  bookName: string;
  chapter: number;
  verseStart: number;
  verseEnd: number;
}

// Browse-view internal shapes (offline-first Chunk 3 §4.A). The browse picker
// reads the curated taxonomy from TWO sources of differing shape: online from
// Supabase (ScriptureTopic / ScriptureTopicVerse, verse text present), offline
// from the shipped refs-only bundle (BundledTopic / BundledTopicVerse, verse
// text resolved from local BSB at render). Both map into these small local
// types so the render is one code path. `bookSlug` is present only on the
// offline path (the field VerseRow needs to resolve text); `verseText` is ''
// offline until resolved, non-empty online.
interface PickerTopic {
  id: string;
  slug: string;
  name: string;
  description: string;
}
interface PickerVerse {
  id: string;
  book: string;
  bookSlug?: string;
  chapter: number;
  verseStart: number;
  verseEnd: number;
  verseText: string;
  referenceText: string;
}

function fromSupabaseTopic(t: ScriptureTopic): PickerTopic {
  return { id: t.id, slug: t.slug, name: t.name, description: t.description };
}
function fromBundleTopic(t: BundledTopic): PickerTopic {
  return { id: t.slug, slug: t.slug, name: t.name, description: t.description };
}
function fromSupabaseVerse(v: ScriptureTopicVerse): PickerVerse {
  return {
    id: v.id,
    book: v.book,
    chapter: v.chapter,
    verseStart: v.verseStart,
    verseEnd: v.verseEnd,
    verseText: v.verseText,
    referenceText: v.referenceText,
  };
}
function fromBundleVerse(v: BundledTopicVerse): PickerVerse {
  return {
    id: `${v.bookSlug}:${v.chapter}:${v.verseStart}-${v.verseEnd}`,
    book: v.book,
    bookSlug: v.bookSlug,
    chapter: v.chapter,
    verseStart: v.verseStart,
    verseEnd: v.verseEnd,
    verseText: '', // resolved from BSB at render (refs-only bundle)
    referenceText: v.referenceText,
  };
}

/**
 * Imperative back handle for the parents' Prev chevron (the AB
 * forwardRef pattern, cf. PrayScriptureSheetHandle). One internal back
 * step (found/browse -> fork). Returns true when consumed; false means
 * the picker is at its root and the parent should run its own exit
 * (clear pick state, return to Q1).
 */
export interface ShareVersePickerHandle {
  goBack(): boolean;
}

interface Props {
  onPick: (verse: SelectedVerse) => void;
  /**
   * Non-null enables the Find-Scripture fork (share-scripture-found
   * arc). The PARENT owns the eligibility gate (caller-is-the-gate,
   * rule #80): VERSE_SUGGEST_ENABLED + en + authed + non-empty rawText +
   * !crisis. Absent means the original browse-only
   * picker, byte-for-byte.
   */
  findPrayer?: Prayer;
}

type Status = 'loading' | 'ready' | 'empty';
type View = 'fork' | 'found' | 'browse';

type FoundState =
  | { kind: 'loading' }
  | { kind: 'verses'; verses: SuggestedVerse[] }
  | { kind: 'silence' }
  | { kind: 'rate-limited' }
  | { kind: 'error' };

/**
 * Verse picker for the share flows (text card + mail card).
 *
 * With `findPrayer` set, opens on a FORK (share-scripture-found arc):
 * "Find Scripture for this prayer" (the curated-verse-match lane via
 * the shared verse-result helper — saved results render instantly and
 * free; a fresh match runs only behind this explicit tap, rule #79
 * pull-based posture) or "Browse and choose my own" (the original
 * picker). The two views cross-link both ways so a change of mind is
 * one tap; Prev (via the goBack handle) steps found/browse -> fork ->
 * parent exit. Without `findPrayer`, this is the original
 * browse-all-topics picker unchanged.
 *
 * Browse view: the user browses the curated taxonomy A→Z (or searches
 * it) and taps a verse. Mirrors the Mail-a-card See-all-topics
 * accordion: tap a topic to expand its verses; the expanded topic row is
 * sticky as you scroll. Verses render full text (no truncation) here —
 * the PNG card grows with content.
 *
 * Found view: matched verses as pick-to-place cards (context sentence +
 * gold ref + resolved BSB text). PICK-ONLY by design: no reader deep
 * links from the share flow (navigating into the reader mid-share would
 * abandon the share). Short entries pick their full segments; chapter-
 * scale entries pick the one-verse excerpt (first red-letter verse,
 * else range start, else verse 1 — rule #80's locked render shape with
 * the whole-chapter fallback, plan decision 10).
 *
 * Search is smart (src/lib/topic-search.ts): name + description, a curated
 * synonym map, and a thresholded typo fallback.
 *
 * No internal Skip button: the "Skip" path lives on the parent's Q1
 * (`bibleVerseSkipBtn`), reached before this picker mounts.
 */
const ShareVersePicker = forwardRef<ShareVersePickerHandle, Props>(
  function ShareVersePicker({ onPick, findPrayer }, ref) {
    const { t } = useTranslation();
    const [status, setStatus] = useState<Status>('loading');
    const [topics, setTopics] = useState<PickerTopic[]>([]);
    const [query, setQuery] = useState('');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [topicVerses, setTopicVerses] = useState<Record<string, PickerVerse[]>>({});
    const [topicLoading, setTopicLoading] = useState<string | null>(null);
    const mountedRef = useRef(true);
    // Offline (§4.A): the bundle ships verses inline, so the offline expand is
    // synchronous from this ref (keyed by topic slug == PickerTopic.id offline).
    // Null while online; non-null routes handleExpandTopic to the bundle.
    const offlineVersesRef = useRef<Map<string, BundledTopicVerse[]> | null>(null);

    const [view, setView] = useState<View>(findPrayer ? 'fork' : 'browse');
    const [found, setFound] = useState<FoundState>({ kind: 'loading' });
    const [foundAttempt, setFoundAttempt] = useState(0);

    // Saved-state label check only (cheap; resolution happens on tap).
    const hasSavedResult = (findPrayer?.verseResult?.entries.length ?? 0) > 0;

    useImperativeHandle(
      ref,
      () => ({
        goBack() {
          if (!findPrayer) return false;
          if (view === 'found' || view === 'browse') {
            setView('fork');
            return true;
          }
          return false; // at the fork: parent exits the verse step
        },
      }),
      [findPrayer, view],
    );

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    // Topic list load (§4.A). Supabase is the online primary; when it yields
    // NOTHING (offline -- robust to navigator.onLine being unreliable on iOS --
    // or unreachable, or an unconfigured deployment) we fall back to the shipped
    // refs-only bundle, whose inline verses are stashed in offlineVersesRef for
    // a synchronous expand (text resolved per-row from local BSB). We also warm
    // the bundle into the SW cache on mount (tiny, ~10 KB, module-cached) so the
    // offline fallback has data after one online use of the picker, independent
    // of a /bible visit -- each deploy rotates the SW cache, so the share flow
    // must self-warm rather than rely on the Bible-surface warmup. Online with a
    // healthy Supabase is byte-for-byte as before (the bundle is never displayed
    // and offlineVersesRef stays null, so the expand path is unchanged).
    useEffect(() => {
      let cancelled = false;
      (async () => {
        void loadTopics().catch(() => {}); // warm topics.json into the SW cache

        let all: ScriptureTopic[] = [];
        try {
          all = await getAllActiveTopics();
        } catch {
          all = [];
        }
        if (cancelled) return;
        if (all.length > 0) {
          setTopics(all.map(fromSupabaseTopic));
          setStatus('ready');
          return;
        }
        // Supabase empty or unreachable: fall back to the offline bundle.
        let bundle: BundledTopic[] = [];
        try {
          bundle = await loadTopics();
        } catch {
          bundle = [];
        }
        if (cancelled) return;
        const versesBySlug = new Map<string, BundledTopicVerse[]>();
        for (const b of bundle) versesBySlug.set(b.slug, b.verses);
        offlineVersesRef.current = bundle.length > 0 ? versesBySlug : null;
        setTopics(bundle.map(fromBundleTopic));
        setStatus(bundle.length > 0 ? 'ready' : 'empty');
      })();
      return () => {
        cancelled = true;
      };
    }, []);

    // The Find lane: saved -> session-cache -> fetch -> persist through
    // the shared helper (verse-result.ts). Runs only while the found
    // view is showing, re-armed by the retry counter.
    useEffect(() => {
      if (view !== 'found' || !findPrayer) return;
      let cancelled = false;
      getVersesForPrayer(findPrayer)
        .then((res) => {
          if (cancelled) return;
          setFound(res ? { kind: 'verses', verses: res } : { kind: 'silence' });
        })
        .catch((err) => {
          if (cancelled) return;
          setFound(
            err instanceof VerseSuggestError && err.code === 'rate-limited'
              ? { kind: 'rate-limited' }
              : { kind: 'error' },
          );
        });
      return () => {
        cancelled = true;
      };
      // The findPrayer OBJECT is deliberately not a dep: the helper's
      // own persist swaps the store object without changing the text,
      // and an identity dep would re-run this fetch on its own write.
      // id + rawText are the identity.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, foundAttempt, findPrayer?.id, findPrayer?.rawText]);

    const enterFound = () => {
      // Reset to loading on every entry so a stale error/silence never
      // flashes; the saved/session-cache paths resolve near-instantly.
      setFound({ kind: 'loading' });
      setView('found');
    };

    const filtered = useMemo(() => searchTopics(query, topics), [query, topics]);

    const handleExpandTopic = async (topic: PickerTopic) => {
      const id = topic.id;
      if (expandedId === id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(id);
      if (topicVerses[id]) return;

      // Bundle mode (topics loaded from the offline bundle): verses ship inline
      // (keyed by slug == id here), resolved from BSB per-row in VerseRow.
      const inline = offlineVersesRef.current?.get(topic.slug);
      if (inline) {
        setTopicVerses((prev) => ({ ...prev, [id]: inline.map(fromBundleVerse) }));
        return;
      }

      // Supabase mode: fetch the topic's verses. If that yields NOTHING (the
      // user went offline since the topic list loaded, so getVersesForTopic
      // returns [] on its swallowed fetch error), fall back to the bundle by
      // slug so the verses still resolve from local BSB (codex pass-1 LOW:
      // same-mount online -> offline transition).
      setTopicLoading(id);
      let verses: ScriptureTopicVerse[] = [];
      try {
        verses = await getVersesForTopic(id);
      } catch {
        verses = [];
      }
      if (!mountedRef.current) return;
      if (verses.length > 0) {
        setTopicVerses((prev) => ({
          ...prev,
          [id]: verses.map(fromSupabaseVerse),
        }));
        setTopicLoading(null);
        return;
      }
      let bundle: BundledTopic[] = [];
      try {
        bundle = await loadTopics();
      } catch {
        bundle = [];
      }
      if (!mountedRef.current) return;
      const fallback = bundle.find((b) => b.slug === topic.slug)?.verses ?? [];
      setTopicVerses((prev) => ({
        ...prev,
        [id]: fallback.map(fromBundleVerse),
      }));
      setTopicLoading(null);
    };

    // --- Fork view ---
    if (view === 'fork' && findPrayer) {
      return (
        <div className={styles.forkWrap}>
          <button
            type="button"
            className={`${styles.forkCard} ${styles.forkCardPrimary}`}
            onClick={enterFound}
          >
            <span className={styles.forkTitle}>
              {t(hasSavedResult ? 'share.verseFindCardSaved' : 'share.verseFindCard')}
            </span>
            <span className={styles.forkSub}>{t('share.verseFindCardSub')}</span>
          </button>
          <button
            type="button"
            className={styles.forkCard}
            onClick={() => setView('browse')}
          >
            <span className={styles.forkTitle}>{t('share.verseBrowseCard')}</span>
          </button>
        </div>
      );
    }

    // --- Found view ---
    if (view === 'found' && findPrayer) {
      if (found.kind === 'loading') {
        return (
          <div
            className={styles.center}
            role="status"
            aria-label={t('share.verseFoundLoadingAria')}
          >
            <div className={styles.pulse} aria-hidden="true" />
          </div>
        );
      }
      if (found.kind === 'verses') {
        return (
          <div className={styles.wrap}>
            <p className={styles.helper}>{t('share.verseFoundHelper')}</p>
            <ul className={styles.foundList}>
              {found.verses.map((v) => (
                <FoundVerseCard key={`${v.category}|${v.ref}`} v={v} onPicked={onPick} />
              ))}
            </ul>
            <button
              type="button"
              className={styles.browseLink}
              onClick={() => setView('browse')}
            >
              {t('share.verseFoundBrowseLink')}
            </button>
          </div>
        );
      }
      if (found.kind === 'error') {
        return (
          <div className={styles.center}>
            <p className={styles.dim}>{t('share.verseFoundError')}</p>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                setFound({ kind: 'loading' });
                setFoundAttempt((a) => a + 1);
              }}
            >
              {t('share.tryAgain')}
            </button>
            <button
              type="button"
              className={styles.browseLink}
              onClick={() => setView('browse')}
            >
              {t('share.verseFoundBrowseLink')}
            </button>
          </div>
        );
      }
      // silence + rate-limited: honest absence, browse one tap away.
      return (
        <div className={styles.center}>
          <p className={styles.dim}>
            {t(
              found.kind === 'rate-limited'
                ? 'share.verseFoundRateLimit'
                : 'share.verseFoundSilence',
            )}
          </p>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => setView('browse')}
          >
            {t('share.verseBrowseCard')}
          </button>
        </div>
      );
    }

    // --- Browse view (the original picker) ---
    if (status === 'loading') {
      return (
        <div className={styles.center} aria-live="polite">
          <div className={styles.pulse} aria-hidden="true" />
          <p className={styles.dim}>{t('share.versePickerLoading')}</p>
        </div>
      );
    }

    if (status === 'empty') {
      return (
        <div className={styles.center}>
          <p className={styles.dim}>{t('share.versePickerUnavailable')}</p>
        </div>
      );
    }

    return (
      <div className={styles.wrap}>
        {findPrayer && (
          <button type="button" className={styles.findLink} onClick={enterFound}>
            {`✦ ${t(hasSavedResult ? 'share.verseFindCardSaved' : 'share.verseFindCard')}`}
          </button>
        )}
        <p className={styles.helper}>{t('share.verseHelper')}</p>

        <div className={styles.searchWrap}>
          <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('share.searchPlaceholder')}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {query && (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => setQuery('')}
              aria-label={t('share.clearSearch')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <p className={styles.dim}>{t('share.searchNoMatch', { query })}</p>
        ) : (
          <ul className={styles.topicList}>
            {filtered.map((topic) => {
              const verses = topicVerses[topic.id] ?? [];
              const isExpanded = expandedId === topic.id;
              const isLoading = topicLoading === topic.id;
              return (
                <li key={topic.id} className={styles.topicItem}>
                  <button
                    type="button"
                    className={styles.topicRow}
                    aria-expanded={isExpanded}
                    onClick={() => handleExpandTopic(topic)}
                  >
                    <span className={styles.topicName}>{topic.name}</span>
                    <svg
                      className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
                      width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {isExpanded && (
                    <div className={styles.topicBody}>
                      {isLoading ? (
                        <p className={styles.dim}>{t('share.versePickerLoading')}</p>
                      ) : verses.length > 0 ? (
                        verses.map((v) => <VerseRow key={v.id} verse={v} onPick={onPick} />)
                      ) : (
                        <p className={styles.dim}>{t('share.versePickerUnavailable')}</p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  },
);

export default ShareVersePicker;

/**
 * A browse-view verse row. ONE code path for online and offline (§4.A): online
 * verses carry `verseText` and render immediately; offline verses (refs-only
 * bundle) carry `bookSlug` and `verseText: ''`, so this row resolves the text
 * from local BSB and stays DISABLED until it has text (so a card is never sent
 * with a blank verse, mirroring FoundVerseCard). The pick is emitted from the
 * row's OWN effective text, not re-read upstream, so the resolved offline text
 * always reaches the card (codex pass-1 LOW).
 */
function VerseRow({
  verse,
  onPick,
}: {
  verse: PickerVerse;
  onPick: (sel: SelectedVerse) => void;
}) {
  const [resolved, setResolved] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const needsResolve = verse.verseText === '' && !!verse.bookSlug;
  const effectiveText = verse.verseText || resolved || '';

  useEffect(() => {
    if (!needsResolve) return;
    mountedRef.current = true;
    ensureBibleHydrated()
      .then(() =>
        resolveTopicVerseText({
          bookSlug: verse.bookSlug as string,
          chapter: verse.chapter,
          verseStart: verse.verseStart,
          verseEnd: verse.verseEnd,
        }),
      )
      .then((txt) => {
        if (mountedRef.current) setResolved(txt);
      })
      .catch(() => {
        if (mountedRef.current) setResolved('');
      });
    return () => {
      mountedRef.current = false;
    };
  }, [
    needsResolve,
    verse.bookSlug,
    verse.chapter,
    verse.verseStart,
    verse.verseEnd,
  ]);

  const handlePick = () => {
    if (!effectiveText) return;
    onPick({
      text: effectiveText,
      reference: verse.referenceText,
      bookName: verse.book,
      chapter: verse.chapter,
      verseStart: verse.verseStart,
      verseEnd: verse.verseEnd,
    });
  };

  return (
    <button
      type="button"
      className={styles.verseRow}
      onClick={handlePick}
      disabled={!effectiveText}
    >
      <span className={styles.verseRef}>{verse.referenceText}</span>
      {effectiveText ? (
        <span className={styles.verseText}>{effectiveText}</span>
      ) : null}
    </button>
  );
}

/**
 * A matched verse as a pick-to-place card: operator context sentence +
 * gold ref + resolved BSB text, the whole card one tap target. Text
 * resolution mirrors the companions sheet's VerseCard (rule #80 render
 * shapes): short entries resolve all segments joined " … "; chapter-
 * scale entries resolve the one-verse excerpt (first red-letter verse,
 * else range start, else verse 1 for a whole-chapter entry — the share-
 * flow extension, plan decision 10). The pick emits the SelectedVerse
 * contract; structured fields carry first-segment start / last-segment
 * end for discontinuous short refs.
 */
function FoundVerseCard({
  v,
  onPicked,
}: {
  v: SuggestedVerse;
  onPicked: (sel: SelectedVerse) => void;
}) {
  const [resolved, setResolved] = useState<string | null>(null);
  const cardMountedRef = useRef(true);

  const anchor =
    v.wjVerses[0] ?? v.segments?.[0]?.[0] ?? (v.wholeChapter ? 1 : null);
  const isShort = v.scale === 'short' && v.segments != null && v.segments.length > 0;
  // Derived, not effect-set: an entry with nothing to resolve (no
  // segments and no anchor) renders as permanently unresolved (''),
  // the same terminal state as a resolution failure.
  const resolvable = isShort || anchor != null;
  const text = resolvable ? resolved : '';

  useEffect(() => {
    if (!resolvable) return;
    cardMountedRef.current = true;
    const want: [number, number][] = isShort
      ? (v.segments as [number, number][])
      : [[anchor as number, anchor as number]];
    ensureBibleHydrated()
      .then(() =>
        Promise.all(
          want.map(([a, b]) =>
            resolveTopicVerseText({
              bookSlug: v.bookSlug,
              chapter: v.chapter,
              verseStart: a,
              verseEnd: b,
            }),
          ),
        ),
      )
      .then((parts) => {
        if (cardMountedRef.current) setResolved(parts.filter(Boolean).join(' … '));
      })
      .catch(() => {
        if (cardMountedRef.current) setResolved('');
      });
    return () => {
      cardMountedRef.current = false;
    };
  }, [v, anchor, isShort, resolvable]);

  const handlePick = () => {
    if (!text) return; // resolution pending or failed; nothing to place
    if (isShort) {
      const segs = v.segments as [number, number][];
      onPicked({
        text,
        reference: v.ref,
        bookName: v.book,
        chapter: v.chapter,
        verseStart: segs[0][0],
        verseEnd: segs[segs.length - 1][1],
      });
      return;
    }
    if (anchor != null) {
      onPicked({
        text,
        reference: `${v.book} ${v.chapter}:${anchor}`,
        bookName: v.book,
        chapter: v.chapter,
        verseStart: anchor,
        verseEnd: anchor,
      });
    }
  };

  const refLabel = isShort ? v.ref : anchor != null ? `${v.book} ${v.chapter}:${anchor}` : v.ref;

  return (
    <li className={styles.foundItem}>
      <button
        type="button"
        className={styles.foundCard}
        onClick={handlePick}
        disabled={!text}
      >
        <span className={styles.foundContext}>{v.context}</span>
        <span className={styles.verseRef}>{`✦ ${refLabel}`}</span>
        {text ? <span className={styles.verseText}>{text}</span> : null}
      </button>
    </li>
  );
}
